import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { faBuilding, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import CompanyService from '../../../services/CompanyService';
import ImageUploaderWithCrop from '../../../components/utils/ImageUploaderWithCrop';
import ColorPicker from '../../../components/utils/ColorPicker';
import Spinner from '../../../components/common/Spinner';
import PageHeader from '../../../components/common/PageHeader';
import '../shared/formsResponsive.css';
import { useNotification } from '../../../contexts/NotificationContext';

// Definir initialFormData fuera del componente para evitar re-renders
const initialFormData = {
    name: '',
    displayName: '',
    address: '',
    phone: '',
    email: '',
    openingTimeMonday: false,
    openingTimeTuesday: false,
    openingTimeWednesday: false,
    openingTimeThursday: false,
    openingTimeFriday: false,
    openingTimeSaturday: false,
    openingTimeSunday: false,
    openingTimeHoliday: false,
    mondayDescription: '',
    tuesdayDescription: '',
    wednesdayDescription: '',
    thursdayDescription: '',
    fridayDescription: '',
    saturdayDescription: '',
    sundayDescription: '',
    holidayDescription: '',
    facebook: false,
    twitter: false,
    linkedin: false,
    instagram: false,
    whatsapp: false,
    facebookLink: '',
    twitterLink: '',
    linkedinLink: '',
    instagramLink: '',
    whatsappLink: '',
    primaryColor: '#2563EB',
    fontPrimaryColor: '#ffffff',
    headerFooterColor: '#ffffff',
    fontHeaderFooterColor: '#000000',
    logoImage: null
};

const CompanyForm = () => {
    const { t } = useTranslation();
    const { success: notifySuccess, error: notifyError } = useNotification();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    
    const companyIdParam = searchParams.get('id');
    const normalizedCompanyId = (() => {
        if (!companyIdParam) return null;
        const trimmed = String(companyIdParam).trim().toLowerCase();
        if (!trimmed || trimmed === 'null' || trimmed === 'undefined') return null;
        const parsed = Number(trimmed);
        return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
    })();
    const isAddMode = location.pathname === '/company-add';

    const [newCompany, setNewCompany] = useState(initialFormData);
    const [activeTab, setActiveTab] = useState('generalInfo');
    const [isLoading, setIsLoading] = useState(!isAddMode);
    const [isEditImage, setIsEditImage] = useState(false);
    const [loadingOperation, setLoadingOperation] = useState(false);
    const [errorMessages, setErrorMessages] = useState({});
    const [generalErrorMessage, setGeneralErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    useEffect(() => {
        // En modo añadir, no hay nada que cargar
        if (isAddMode) {
            return;
        }

        // Si el id no es válido en modo edición, evitar llamada inválida al backend
        if (!normalizedCompanyId) {
            setGeneralErrorMessage(t('common.errorOccurred'));
            return;
        }

        // Solo en modo editar, cargar datos
        const fetchData = async () => {
            setIsLoading(true);
            setLoadingOperation(true);
            try {
                const response = await CompanyService.getById(normalizedCompanyId);
                
                setIsLoading(false);

                if (response.logoImage) {
                    const event = new CustomEvent('logoUpdated', { detail: { logoUrl: response.logoImage } });
                    window.dispatchEvent(event);
                }

                const normalizedCompany = {
                    ...initialFormData,
                    ...response,
                    openingTimeMonday: response.mondayIsOpen ?? response.openingTimeMonday ?? false,
                    openingTimeTuesday: response.tuesdayIsOpen ?? response.openingTimeTuesday ?? false,
                    openingTimeWednesday: response.wednesdayIsOpen ?? response.openingTimeWednesday ?? false,
                    openingTimeThursday: response.thursdayIsOpen ?? response.openingTimeThursday ?? false,
                    openingTimeFriday: response.fridayIsOpen ?? response.openingTimeFriday ?? false,
                    openingTimeSaturday: response.saturdayIsOpen ?? response.openingTimeSaturday ?? false,
                    openingTimeSunday: response.sundayIsOpen ?? response.openingTimeSunday ?? false,
                    openingTimeHoliday: response.holidayIsOpen ?? response.openingTimeHoliday ?? false,
                    mondayDescription: response.mondayDescription ?? '',
                    tuesdayDescription: response.tuesdayDescription ?? '',
                    wednesdayDescription: response.wednesdayDescription ?? '',
                    thursdayDescription: response.thursdayDescription ?? '',
                    fridayDescription: response.fridayDescription ?? '',
                    saturdayDescription: response.saturdayDescription ?? '',
                    sundayDescription: response.sundayDescription ?? '',
                    holidayDescription: response.holidayDescription ?? '',
                    facebook: response.facebook ?? false,
                    twitter: response.twitter ?? false,
                    linkedin: response.linkedin ?? false,
                    instagram: response.instagram ?? false,
                    whatsapp: response.whatsapp ?? false,
                    facebookLink: response.facebookLink ?? '',
                    twitterLink: response.twitterLink ?? '',
                    linkedinLink: response.linkedinLink ?? '',
                    instagramLink: response.instagramLink ?? '',
                    whatsappLink: response.whatsappLink ?? '',
                };

                setNewCompany(normalizedCompany);
                if (response.primaryColor) {
                    document.documentElement.style.setProperty('--button-background-color', response.primaryColor);
                }
                if (response.fontPrimaryColor) {
                    document.documentElement.style.setProperty('--button-font-color', response.fontPrimaryColor);
                }
                if (response.headerFooterColor) {
                    document.documentElement.style.setProperty('--header-footer-background-color', response.headerFooterColor);
                }
                if (response.fontHeaderFooterColor) {
                    document.documentElement.style.setProperty('--header-footer-font-color', response.fontHeaderFooterColor);
                }
            } catch (error) {
                setGeneralErrorMessage(error.message || t('common.errorOccurred'));
                setIsLoading(false);
            } finally {
                setLoadingOperation(false);
            }
        };

        fetchData();
    }, [isAddMode, normalizedCompanyId, t]);

    useEffect(() => {
        if (generalErrorMessage) {
            notifyError(generalErrorMessage, { autoClose: 3000, placement: 'centered' });
            setGeneralErrorMessage('');
        }
    }, [generalErrorMessage, notifyError]);

    useEffect(() => {
        if (successMessage) {
            notifySuccess(successMessage, { autoClose: 3000, placement: 'centered' });
            setSuccessMessage('');
        }
    }, [successMessage, notifySuccess]);

    const handleInputChange = (field, value) => {
        setNewCompany(prev => ({
            ...prev,
            [field]: value
        }));
        
        if (errorMessages[field]) {
            setErrorMessages(prev => ({
                ...prev,
                [field]: ''
            }));
        }
    };

    const handleCheckboxChange = (field) => {
        setNewCompany(prev => ({
            ...prev,
            [field]: !prev[field]
        }));
    };

    const handleImageSelect = (file) => {
        setNewCompany(prev => ({
            ...prev,
            logoImage: file
        }));
        setIsEditImage(true);
    };

    const validateForm = () => {
        const errors = {};
        
        // Campo name es obligatorio
        if (!newCompany.name || newCompany.name.trim() === '') {
            errors.name = t('company.errors.nameRequired');
        }
        if (!newCompany.displayName || newCompany.displayName.trim() === '') {
            errors.displayName = t('company.errors.displayNameRequired');
        }

        // Validaciones condicionales para horarios
        if (newCompany.openingTimeMonday && !newCompany.mondayDescription) {
            errors.mondayDescription = t('company.errors.mondayDescriptionRequired');
        }
        if (newCompany.openingTimeTuesday && !newCompany.tuesdayDescription) {
            errors.tuesdayDescription = t('company.errors.tuesdayDescriptionRequired');
        }
        if (newCompany.openingTimeWednesday && !newCompany.wednesdayDescription) {
            errors.wednesdayDescription = t('company.errors.wednesdayDescriptionRequired');
        }
        if (newCompany.openingTimeThursday && !newCompany.thursdayDescription) {
            errors.thursdayDescription = t('company.errors.thursdayDescriptionRequired');
        }
        if (newCompany.openingTimeFriday && !newCompany.fridayDescription) {
            errors.fridayDescription = t('company.errors.fridayDescriptionRequired');
        }
        if (newCompany.openingTimeSaturday && !newCompany.saturdayDescription) {
            errors.saturdayDescription = t('company.errors.saturdayDescriptionRequired');
        }
        if (newCompany.openingTimeSunday && !newCompany.sundayDescription) {
            errors.sundayDescription = t('company.errors.sundayDescriptionRequired');
        }
        if (newCompany.openingTimeHoliday && !newCompany.holidayDescription) {
            errors.holidayDescription = t('company.errors.holidayDescriptionRequired');
        }

        // Validaciones condicionales para redes sociales
        if (newCompany.facebook && !newCompany.facebookLink) {
            errors.facebookLink = t('company.errors.facebookLinkRequired');
        }
        if (newCompany.twitter && !newCompany.twitterLink) {
            errors.twitterLink = t('company.errors.twitterLinkRequired');
        }
        if (newCompany.linkedin && !newCompany.linkedinLink) {
            errors.linkedinLink = t('company.errors.linkedinLinkRequired');
        }
        if (newCompany.instagram && !newCompany.instagramLink) {
            errors.instagramLink = t('company.errors.instagramLinkRequired');
        }
        if (newCompany.whatsapp && !newCompany.whatsappLink) {
            errors.whatsappLink = t('company.errors.whatsappLinkRequired');
        }

        return errors;
    };

    const handleSave = async () => {
        const errors = validateForm();
        
        if (Object.keys(errors).length > 0) {
            setErrorMessages(errors);
            setGeneralErrorMessage(t('company.errors.fixIssues', { issues: Object.values(errors).join(', ') }));
            return;
        }

        setLoadingOperation(true);
        setGeneralErrorMessage('');

        try {
            if (isAddMode) {
                // CREATE: Enviar JSON (sin imagen)
                const jsonData = {
                    name: newCompany.name?.trim() || '',
                    displayName: newCompany.displayName?.trim() || '',
                    address: newCompany.address?.trim() || '',
                    phone: newCompany.phone?.trim() || '',
                    email: newCompany.email?.trim() || '',
                    mondayIsOpen: !!newCompany.openingTimeMonday,
                    tuesdayIsOpen: !!newCompany.openingTimeTuesday,
                    wednesdayIsOpen: !!newCompany.openingTimeWednesday,
                    thursdayIsOpen: !!newCompany.openingTimeThursday,
                    fridayIsOpen: !!newCompany.openingTimeFriday,
                    saturdayIsOpen: !!newCompany.openingTimeSaturday,
                    sundayIsOpen: !!newCompany.openingTimeSunday,
                    holidayIsOpen: !!newCompany.openingTimeHoliday,
                    mondayDescription: newCompany.mondayDescription || '',
                    tuesdayDescription: newCompany.tuesdayDescription || '',
                    wednesdayDescription: newCompany.wednesdayDescription || '',
                    thursdayDescription: newCompany.thursdayDescription || '',
                    fridayDescription: newCompany.fridayDescription || '',
                    saturdayDescription: newCompany.saturdayDescription || '',
                    sundayDescription: newCompany.sundayDescription || '',
                    holidayDescription: newCompany.holidayDescription || '',
                    facebook: !!newCompany.facebook,
                    twitter: !!newCompany.twitter,
                    linkedin: !!newCompany.linkedin,
                    instagram: !!newCompany.instagram,
                    whatsapp: !!newCompany.whatsapp,
                    facebookLink: newCompany.facebookLink || '',
                    twitterLink: newCompany.twitterLink || '',
                    linkedinLink: newCompany.linkedinLink || '',
                    instagramLink: newCompany.instagramLink || '',
                    whatsappLink: newCompany.whatsappLink || '',
                    primaryColor: newCompany.primaryColor || '',
                    fontPrimaryColor: newCompany.fontPrimaryColor || '',
                    headerFooterColor: newCompany.headerFooterColor || '',
                    fontHeaderFooterColor: newCompany.fontHeaderFooterColor || ''
                };

                const createdCompany = await CompanyService.create(jsonData);

                // Si hay imagen, hacer UPDATE con FormData
                if (newCompany.logoImage) {
                    const formData = new FormData();
                    Object.keys(jsonData).forEach(key => {
                        formData.append(key, jsonData[key]);
                    });

                    if (typeof newCompany.logoImage === 'string' && newCompany.logoImage.startsWith('data:image/')) {
                        const blob = await fetch(newCompany.logoImage).then(r => r.blob());
                        formData.append('logoImage', blob, 'logo.png');
                    } else if (newCompany.logoImage instanceof File) {
                        formData.append('logoImage', newCompany.logoImage);
                    }

                    await CompanyService.updateCompany(createdCompany.id, formData);
                }

                // Disparar evento para recargar lista de empresas en header
                window.dispatchEvent(new CustomEvent('companiesUpdated'));

                setSuccessMessage(t('company.success.created'));
                
                // Resetear campos del formulario después de crear exitosamente
                setNewCompany(initialFormData);
                setIsEditImage(false);
            } else {
                if (!normalizedCompanyId) {
                    setGeneralErrorMessage(t('common.errorOccurred'));
                    return;
                }

                // UPDATE: Enviar FormData (con o sin imagen)
                const normalizedData = {
                    ...initialFormData,
                    ...newCompany,
                    openingTimeMonday: !!newCompany.openingTimeMonday,
                    openingTimeTuesday: !!newCompany.openingTimeTuesday,
                    openingTimeWednesday: !!newCompany.openingTimeWednesday,
                    openingTimeThursday: !!newCompany.openingTimeThursday,
                    openingTimeFriday: !!newCompany.openingTimeFriday,
                    openingTimeSaturday: !!newCompany.openingTimeSaturday,
                    openingTimeSunday: !!newCompany.openingTimeSunday,
                    openingTimeHoliday: !!newCompany.openingTimeHoliday,
                    mondayIsOpen: !!newCompany.openingTimeMonday,
                    tuesdayIsOpen: !!newCompany.openingTimeTuesday,
                    wednesdayIsOpen: !!newCompany.openingTimeWednesday,
                    thursdayIsOpen: !!newCompany.openingTimeThursday,
                    fridayIsOpen: !!newCompany.openingTimeFriday,
                    saturdayIsOpen: !!newCompany.openingTimeSaturday,
                    sundayIsOpen: !!newCompany.openingTimeSunday,
                    holidayIsOpen: !!newCompany.openingTimeHoliday,
                    mondayDescription: newCompany.mondayDescription || '',
                    tuesdayDescription: newCompany.tuesdayDescription || '',
                    wednesdayDescription: newCompany.wednesdayDescription || '',
                    thursdayDescription: newCompany.thursdayDescription || '',
                    fridayDescription: newCompany.fridayDescription || '',
                    saturdayDescription: newCompany.saturdayDescription || '',
                    sundayDescription: newCompany.sundayDescription || '',
                    holidayDescription: newCompany.holidayDescription || ''
                };

                const formData = new FormData();
                const { logoImage, ...payloadWithoutLogo } = normalizedData;

                Object.entries(payloadWithoutLogo).forEach(([key, value]) => {
                    if (value !== undefined && value !== null) {
                        formData.set(key, typeof value === 'boolean' ? String(value) : value);
                    }
                });

                if (isEditImage && logoImage) {
                    if (typeof logoImage === 'string' && logoImage.startsWith('data:image/')) {
                        const blob = await fetch(logoImage).then(r => r.blob());
                        formData.set('logoImage', blob, 'logo.png');
                    } else if (logoImage instanceof File) {
                        formData.set('logoImage', logoImage);
                    }
                }

                await CompanyService.updateCompany(normalizedCompanyId, formData);

                // Disparar evento para recargar lista de empresas en header
                window.dispatchEvent(new CustomEvent('companiesUpdated'));

                setSuccessMessage(t('company.success.updated'));
                
                // Recargar datos del servidor después de actualizar exitosamente
                try {
                    const response = await CompanyService.getById(normalizedCompanyId);
                    
                    if (response.logoImage) {
                        const event = new CustomEvent('logoUpdated', { detail: { logoUrl: response.logoImage } });
                        window.dispatchEvent(event);
                    }

                    const normalizedCompany = {
                        ...initialFormData,
                        ...response,
                        openingTimeMonday: response.mondayIsOpen ?? response.openingTimeMonday ?? false,
                        openingTimeTuesday: response.tuesdayIsOpen ?? response.openingTimeTuesday ?? false,
                        openingTimeWednesday: response.wednesdayIsOpen ?? response.openingTimeWednesday ?? false,
                        openingTimeThursday: response.thursdayIsOpen ?? response.openingTimeThursday ?? false,
                        openingTimeFriday: response.fridayIsOpen ?? response.openingTimeFriday ?? false,
                        openingTimeSaturday: response.saturdayIsOpen ?? response.openingTimeSaturday ?? false,
                        openingTimeSunday: response.sundayIsOpen ?? response.openingTimeSunday ?? false,
                        openingTimeHoliday: response.holidayIsOpen ?? response.openingTimeHoliday ?? false,
                        mondayDescription: response.mondayDescription ?? '',
                        tuesdayDescription: response.tuesdayDescription ?? '',
                        wednesdayDescription: response.wednesdayDescription ?? '',
                        thursdayDescription: response.thursdayDescription ?? '',
                        fridayDescription: response.fridayDescription ?? '',
                        saturdayDescription: response.saturdayDescription ?? '',
                        sundayDescription: response.sundayDescription ?? '',
                        holidayDescription: response.holidayDescription ?? '',
                        facebook: response.facebook ?? false,
                        twitter: response.twitter ?? false,
                        linkedin: response.linkedin ?? false,
                        instagram: response.instagram ?? false,
                        whatsapp: response.whatsapp ?? false,
                        facebookLink: response.facebookLink ?? '',
                        twitterLink: response.twitterLink ?? '',
                        linkedinLink: response.linkedinLink ?? '',
                        instagramLink: response.instagramLink ?? '',
                        whatsappLink: response.whatsappLink ?? '',
                    };

                    setNewCompany(normalizedCompany);
                    setIsEditImage(false);
                    
                    if (response.primaryColor) {
                        document.documentElement.style.setProperty('--button-background-color', response.primaryColor);
                    }
                    if (response.fontPrimaryColor) {
                        document.documentElement.style.setProperty('--button-font-color', response.fontPrimaryColor);
                    }
                    if (response.headerFooterColor) {
                        document.documentElement.style.setProperty('--header-footer-background-color', response.headerFooterColor);
                    }
                    if (response.fontHeaderFooterColor) {
                        document.documentElement.style.setProperty('--header-footer-font-color', response.fontHeaderFooterColor);
                    }
                } catch (reloadError) {
                    // Si falla la recarga, no hacer nada (los datos ya se actualizaron)
                    console.error('Error al recargar datos después de actualizar:', reloadError);
                }
            }
        } catch (error) {
            setGeneralErrorMessage(error.message || t('common.errorOccurred'));
        } finally {
            setLoadingOperation(false);
        }
    };

    const handleCancel = () => {
        navigate('/company');
    };

    if (isLoading) {
        return <Spinner overlay={true} message={t('common.loading')} />;
    }

    return (
        <>
            {loadingOperation && <Spinner overlay={true} message={t('common.processing')} />}
            
            <PageHeader
                title={isAddMode ? t('company.add') : t('company.edit')}
                subtitle={t('company.subtitle')}
                icon={faBuilding}
            >
                <button
                    className="btn btn-primary"
                    onClick={handleCancel}
                >
                    <FontAwesomeIcon icon={faArrowLeft} /> {t('common.goBack')}
                </button>
            </PageHeader>

            <section className="panel">
                <div className="panel-body">
                <div className="tabs">
                    <ul className="nav nav-tabs">
                        <li className={activeTab === 'generalInfo' ? 'active' : ''}>
                            <a href="#generalInfo" onClick={(e) => { e.preventDefault(); setActiveTab('generalInfo'); }} style={{ cursor: 'pointer' }}>
                                {t('company.tabs.generalInfo')}
                            </a>
                        </li>
                        <li className={activeTab === 'workingHours' ? 'active' : ''}>
                            <a href="#workingHours" onClick={(e) => { e.preventDefault(); setActiveTab('workingHours'); }} style={{ cursor: 'pointer' }}>
                                {t('company.tabs.workingHours')}
                            </a>
                        </li>
                        <li className={activeTab === 'socialMedia' ? 'active' : ''}>
                            <a href="#socialMedia" onClick={(e) => { e.preventDefault(); setActiveTab('socialMedia'); }} style={{ cursor: 'pointer' }}>
                                {t('company.tabs.socialMedia')}
                            </a>
                        </li>
                        <li className={activeTab === 'themes' ? 'active' : ''}>
                            <a href="#themes" onClick={(e) => { e.preventDefault(); setActiveTab('themes'); }} style={{ cursor: 'pointer' }}>
                                {t('company.tabs.themes')}
                            </a>
                        </li>
                    </ul>

                    <div className="tab-content">
                        {activeTab === 'generalInfo' && (
                            <div className="tab-pane active">
                                <div className="form-group">
                                    <label className="control-label">
                                        {t('company.generalInfo.name')}
                                        <span className="required">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        className={`form-control ${errorMessages.name ? 'has-error' : ''}`}
                                        value={newCompany.name || ''}
                                        onChange={(e) => handleInputChange('name', e.target.value)}
                                        required
                                    />
                                    {errorMessages.name && <div className="field-error-message">{errorMessages.name}</div>}
                                </div>

                                <div className="form-group">
                                    <label className="control-label">
                                        {t('company.generalInfo.displayName')}
                                        <span className="required">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        className={`form-control ${errorMessages.displayName ? 'has-error' : ''}`}
                                        value={newCompany.displayName || ''}
                                        onChange={(e) => handleInputChange('displayName', e.target.value)}
                                        required
                                    />
                                    {errorMessages.displayName && <div className="field-error-message">{errorMessages.displayName}</div>}
                                </div>

                                <div className="form-group">
                                    <label className="control-label">{t('company.generalInfo.address')}</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={newCompany.address || ''}
                                        onChange={(e) => handleInputChange('address', e.target.value)}
                                    />
                                </div>

                                <div className="form-group">
                                    <label className="control-label">{t('company.generalInfo.phone')}</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={newCompany.phone || ''}
                                        onChange={(e) => handleInputChange('phone', e.target.value)}
                                    />
                                </div>

                                <div className="form-group">
                                    <label className="control-label">{t('company.generalInfo.email')}</label>
                                    <input
                                        type="email"
                                        className="form-control"
                                        value={newCompany.email || ''}
                                        onChange={(e) => handleInputChange('email', e.target.value)}
                                    />
                                </div>
                            </div>
                        )}

                        {activeTab === 'workingHours' && (
                            <div className="tab-pane active">
                                {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday', 'holiday'].map(day => (
                                    <div key={day} className="form-group" style={{ marginBottom: '15px' }}>
                                        <div className="company-inline-toggle-row">
                                            <label className="company-inline-toggle-label" style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontWeight: 'normal', margin: 0 }}>
                                                <input
                                                    type="checkbox"
                                                    id={`openingTime${day.charAt(0).toUpperCase() + day.slice(1)}`}
                                                    checked={newCompany[`openingTime${day.charAt(0).toUpperCase() + day.slice(1)}`] || false}
                                                    onChange={() => handleCheckboxChange(`openingTime${day.charAt(0).toUpperCase() + day.slice(1)}`)}
                                                    style={{ marginRight: '8px', width: '18px', height: '18px', cursor: 'pointer' }}
                                                />
                                                <span>{t(`company.workingHours.${day}`)}</span>
                                            </label>
                                            {newCompany[`openingTime${day.charAt(0).toUpperCase() + day.slice(1)}`] && (
                                                <div className="company-inline-toggle-input">
                                                    <input
                                                        type="text"
                                                        className={`form-control ${errorMessages[`${day}Description`] ? 'has-error' : ''}`}
                                                        placeholder={t(`company.workingHours.${day}Description`)}
                                                        value={newCompany[`${day}Description`] || ''}
                                                        onChange={(e) => handleInputChange(`${day}Description`, e.target.value)}
                                                    />
                                                    {errorMessages[`${day}Description`] && (
                                                        <div className="field-error-message">{errorMessages[`${day}Description`]}</div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {activeTab === 'socialMedia' && (
                            <div className="tab-pane active">
                                {['facebook', 'twitter', 'linkedin', 'instagram', 'whatsapp'].map(platform => (
                                    <div key={platform} className="form-group" style={{ marginBottom: '15px' }}>
                                        <div className="company-inline-toggle-row">
                                            <label className="company-inline-toggle-label" style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontWeight: 'normal', margin: 0 }}>
                                                <input
                                                    type="checkbox"
                                                    id={platform}
                                                    checked={newCompany[platform] || false}
                                                    onChange={() => handleCheckboxChange(platform)}
                                                    style={{ marginRight: '8px', width: '18px', height: '18px', cursor: 'pointer' }}
                                                />
                                                <span>{t(`company.socialMedia.${platform}`)}</span>
                                            </label>
                                            {newCompany[platform] && (
                                                <div className="company-inline-toggle-input">
                                                    <input
                                                        type="text"
                                                        className={`form-control ${errorMessages[`${platform}Link`] ? 'has-error' : ''}`}
                                                        placeholder={t(`company.socialMedia.${platform}Link`)}
                                                        value={newCompany[`${platform}Link`] || ''}
                                                        onChange={(e) => handleInputChange(`${platform}Link`, e.target.value)}
                                                    />
                                                    {errorMessages[`${platform}Link`] && (
                                                        <div className="field-error-message">{errorMessages[`${platform}Link`]}</div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {activeTab === 'themes' && (
                            <div className="tab-pane active">
                                <div className="form-group">
                                    <label className="control-label">{t('company.themes.logo')}</label>
                                    {!isEditImage &&
                                    typeof newCompany.logoImage === 'string' &&
                                    newCompany.logoImage.trim() !== '' &&
                                    !newCompany.logoImage.startsWith('data:image/') ? (
                                        <div className="image-preview-block">
                                            <img
                                                src={newCompany.logoImage}
                                                alt={t('company.themes.logo')}
                                                className="preview-image-modal"
                                            />
                                            <button
                                                type="button"
                                                className="btn btn-secondary image-preview-action"
                                                onClick={() => setIsEditImage(true)}
                                            >
                                                {t('company.themes.changeImage')}
                                            </button>
                                        </div>
                                    ) : (
                                        <ImageUploaderWithCrop onImageChange={handleImageSelect} />
                                    )}
                                </div>

                                <div className="form-group">
                                    <label className="control-label">{t('company.themes.primaryColor')}</label>
                                    <ColorPicker
                                        value={newCompany.primaryColor || '#2563EB'}
                                        onColorChange={(color) => handleInputChange('primaryColor', color)}
                                    />
                                </div>

                                <div className="form-group">
                                    <label className="control-label">{t('company.themes.fontPrimaryColor')}</label>
                                    <ColorPicker
                                        value={newCompany.fontPrimaryColor || '#ffffff'}
                                        onColorChange={(color) => handleInputChange('fontPrimaryColor', color)}
                                    />
                                </div>

                                <div className="form-group">
                                    <label className="control-label">{t('company.themes.headerFooterColor')}</label>
                                    <ColorPicker
                                        value={newCompany.headerFooterColor || '#ffffff'}
                                        onColorChange={(color) => handleInputChange('headerFooterColor', color)}
                                    />
                                </div>

                                <div className="form-group">
                                    <label className="control-label">{t('company.themes.fontHeaderFooterColor')}</label>
                                    <ColorPicker
                                        value={newCompany.fontHeaderFooterColor || '#000000'}
                                        onColorChange={(color) => handleInputChange('fontHeaderFooterColor', color)}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <div className="panel-footer" style={{ textAlign: 'right', padding: '15px' }}>
                    <button
                        type="button"
                        className="btn btn-default"
                        onClick={handleCancel}
                        style={{ marginRight: '10px' }}
                    >
                        {t('common.cancel')}
                    </button>
                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleSave}
                    >
                        {t('common.save')}
                    </button>
                </div>
            </div>
        </section>
        </>
    );
};

export default CompanyForm;

